/**
 * Step 1 — Company Setup [MANDATORY — HARD GATE]
 *
 * Embeds IntegrationsPage (the real companies screen) with hideHeader=true
 * so only the "Add Company" action button is shown — no title/icon chrome.
 *
 * Bottom bar is blocked until at least 1 company exists.
 * Polls every 3 s so the gate unlocks automatically after a company is added.
 */

import { useState, useEffect, useCallback } from 'react';
import IntegrationsPage from '@/components/Pages/IntegrationsPage';
import { WizardBottomBar } from '@/components/onboarding/WizardBottomBar';
import { mockIntegrationsService } from '@/data/mockIntegrationsData';

interface Step1CompanySetupProps {
  onNext: () => void;
}

export function Step1CompanySetup({ onNext }: Step1CompanySetupProps) {
  const [companyCount, setCompanyCount] = useState<number>(0);
  const [isChecking, setIsChecking] = useState(true);

  const checkCompanies = useCallback(async () => {
    try {
      const companies = await mockIntegrationsService.getCompanies();
      // Filter out DEFAULT company and inactive
      const real = companies.filter((c) => c.code !== 'DEFAULT' && c.status === 'active');
      setCompanyCount(real.length);
    } catch {
      setCompanyCount(0);
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    checkCompanies();
    // Poll every 3 s so the gate unlocks automatically after a company is added
    const interval = setInterval(checkCompanies, 3000);
    return () => clearInterval(interval);
  }, [checkCompanies]);

  const hasCompany = companyCount >= 1;

  const helperText = hasCompany ? (
    <span className="text-green-600 font-medium">
      {companyCount} {companyCount === 1 ? 'company' : 'companies'} added ✓
    </span>
  ) : (
    <span>
      You must add at least one company before you can continue. Companies are the foundation of
      Access Time — all time entries, user access, and overtime types are scoped to a company.
    </span>
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-hidden px-3 pt-3 pb-1">
        <div className="flex flex-col h-full min-h-0 border border-border rounded-lg bg-white overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            <IntegrationsPage hideHeader />
          </div>
        </div>
      </div>

      <WizardBottomBar
        currentStep={1}
        helperText={isChecking ? 'Checking companies…' : helperText}
        nextDisabled={!hasCompany}
        hideBack={true}
        hideSkip={true}
        onNext={onNext}
      />
    </div>
  );
}
